/* eslint-disable */
import SocketKeepAlive from './socketKeepAlive';

const setupSocketListeners = Symbol('setupSocketListeners');

/**
 * This behaves like a WebSocket in every way, except if it fails to connect,
 * or it gets disconnected, it will repeatedly poll until it successfully connects
 * again.
 */
class Socket {
  // --Public variables--

  /** The number of attempted reconnects since starting, or the last successful connection. */
  reconnectAttempts = 0;

  /**
   * The current state of the connection.
   * Can be one of: WebSocket.CONNECTING, WebSocket.OPEN, WebSocket.CLOSING, WebSocket.CLOSED
   */
  readyState = WebSocket.CONNECTING;

  /**
   * A string indicating the name of the sub-protocol the server selected; this will be one of
   * the strings specified in the protocols parameter when creating the WebSocket object.
   */

  protocol = null;

  // --Private variables--

  _ws = false;

  _forcedClose = false;

  _timedOut = false;

  _eventTarget = document.createElement('div');

  _heartBeat = () => {};

  _queue = [];

  // --Static variables--

  /**
   * Whether all instances of Socket should log debug messages.
   * Setting this to true is the equivalent of setting all instances of Socket.debug to true.
   */

  static debugAll = false;

  static CONNECTING = WebSocket.CONNECTING;

  static OPEN = WebSocket.OPEN;

  static CLOSING = WebSocket.CLOSING;

  static CLOSED = WebSocket.CLOSED;

  static CONNECTIONS = {};

  /**
   * Parameters
   * ==========
   * url - The url you are connecting to.
   * protocols - Optional string or array of protocols.
   * options can either be passed upon instantiation or set after instantiation.
   */
  constructor(url, protocols, options = {}) {
    const defaults = {
      /** Whether this instance should log debug messages. */
      debug: false,

      /** Whether or not the websocket should attempt to connect immediately upon instantiation. */
      automaticOpen: true,

      /** The number of milliseconds to delay before attempting to reconnect. */
      reconnectInterval: 1000,
      /** The maximum number of milliseconds to delay a reconnection attempt. */
      maxReconnectInterval: 30000,
      /** The rate of increase of the reconnect delay. Allows reconnect attempts to back off when problems persist. */
      reconnectDecay: 1.5,

      /** The maximum time in milliseconds to wait for a connection to succeed before closing and retrying. */
      timeoutInterval: 2000,

      /** The maximum number of reconnection attempts to make. Unlimited if null. */
      maxReconnectAttempts: null,

      /** The binary type, possible values 'blob' or 'arraybuffer', default 'blob'. */
      binaryType: 'blob',

      /** In the event that the user is idle it'll attempt to send a data packet. */
      keepAlive: true,

      keepAliveIntervalTime: 60 * 1000 * 5, // 5 mins
    };

    // Overwrite and define settings with options if they exist.
    for (const key in defaults) {
      if (typeof options[key] !== 'undefined') {
        this[key] = options[key];
      } else {
        this[key] = defaults[key];
      }
    }

    this.url = url;
    this.protocols = protocols;

    // Expose event listener API
    this.addEventListener = this._eventTarget.addEventListener.bind(
      this._eventTarget
    );
    this.removeEventListener = this._eventTarget.removeEventListener.bind(
      this._eventTarget
    );
    this.dispatchEvent = this._eventTarget.dispatchEvent.bind(
      this._eventTarget
    );

    const self = this;

    this.addEventListener('open', event => self.onopen && self.onopen(event));
    this.addEventListener(
      'close',
      event => self.onclose && self.onclose(event)
    );
    this.addEventListener(
      'connecting',
      event => self.onconnecting && self.onconnecting(event)
    );
    this.addEventListener(
      'message',
      event => self.onmessage && self.onmessage(event)
    );
    this.addEventListener(
      'error',
      event => self.onerror && self.onerror(event)
    );

    if (this.keepAlive) {
      const keepAlive = new SocketKeepAlive({
        keepAliveCallback: _ => {
          const ping = JSON.stringify({ action: 'ping' });

          if (self._ws) self._ws.send(ping);
        },
        intervalTime: this.keepAliveIntervalTime,
      });

      this._heartBeat = keepAlive.heartBeat;
    }

    // Whether or not to create a websocket upon instantiation
    if (this.automaticOpen == true) this.open(false);
    this.onmessage = undefined;
  }

  open(reconnectAttempt = false) {
    if (
      Socket.CONNECTIONS[this.url] &&
      Socket.CONNECTIONS[this.url].readyState == Socket.OPEN
    ) {
      this._ws = Socket.CONNECTIONS[this.url];

      this[setupSocketListeners](reconnectAttempt, true);
    } else {
      this._ws = new WebSocket(this.url, this.protocols || []);
      this._ws.binaryType = this.binaryType;
      Socket.CONNECTIONS[this.url] = this._ws;

      this[setupSocketListeners](reconnectAttempt, false);
    }

    if (reconnectAttempt) {
      if (
        this.maxReconnectAttempts &&
        this.reconnectAttempts > this.maxReconnectAttempts
      )
        return;
    } else {
      this._eventTarget.dispatchEvent(generateEvent('connecting'));
      this.reconnectAttempts = 0;
    }

    if (this.debug || Socket.debugAll)
      console.debug('Socket', 'attempt-connect', this.url);
  }

  /**
   * Transmits data to the server over the WebSocket connection.
   *
   * @param data a text string, ArrayBuffer or Blob to send to the server.
   */
  send(data) {
    if (this._ws) {
      if (this.debug || Socket.debugAll)
        console.debug('Socket', 'send', this.url, data);

      this._heartBeat();

      if (this._ws.readyState !== this._ws.OPEN) {
        this.addQueue(data);
        return;
      }

      return this._ws.send(data);
    }
    this.addQueue(data);
  }

  sendMessage(action, message) {
    this.send(
      JSON.stringify({
        action,
        data: message,
      })
    );
  }

  /**
   * Closes the WebSocket connection or connection attempt, if any.
   * If the connection is already CLOSED, this method does nothing.
   */
  close(code = 1000, reason) {
    this._forcedClose = true;

    if (this._ws) this._ws.close(code, reason);
  }

  addQueue(message) {
    this._queue.push(message);
  }

  isQueueEmpty() {
    return this._queue.length === 0;
  }

  flushQueue() {
    while (!this.isQueueEmpty()) {
      const message = this._queue.shift();
      this.send(message);
    }
  }

  /**
   * Additional public API method to refresh the connection if still open (close, re-open).
   * For example, if the app suspects bad data / missed heart beats, it can try to refresh.
   */
  refresh() {
    if (this._ws) this._ws.close();
  }

  /**
   * Setup WebSocket callback functions and apply retry logic
   */
  [setupSocketListeners](reconnectAttempt, hasConnection) {
    const ws = this._ws;
    const self = this;

    const intervalTimeout = !hasConnection
      ? setTimeout(function() {
          if (self.debug || Socket.debugAll) {
            console.debug('Socket', 'connection-timeout', self.url);
          }
          self._timedOut = true;
          self._ws.close();
          self._timedOut = false;
        }, this.timeoutInterval)
      : null;

    ws.onopen = function(event) {
      clearTimeout(intervalTimeout);

      if (self.debug || Socket.debugAll)
        console.debug('Socket', 'onopen', self.url);

      self.protocol = self._ws.protocol;
      self.readyState = WebSocket.OPEN;
      self.reconnectAttempts = 0;

      const e = generateEvent('open');
      e.isReconnect = reconnectAttempt;
      reconnectAttempt = false;

      self._eventTarget.dispatchEvent(e);

      if (!self.isQueueEmpty()) {
        self.flushQueue();
      }
    };

    ws.onclose = function(event) {
      clearTimeout(intervalTimeout);
      self._ws = null;

      if (self._forcedClose) {
        self.readyState = WebSocket.CLOSED;
        self._eventTarget.dispatchEvent(generateEvent('close'));
      } else {
        self.readyState = WebSocket.CONNECTING;

        const e = generateEvent('connecting');
        e.code = event.code;
        e.reason = event.reason;
        e.wasClean = event.wasClean;

        self._eventTarget.dispatchEvent(e);

        if (!reconnectAttempt && !self._timedOut) {
          if (self.debug || Socket.debugAll)
            console.debug('Socket', 'onclose', self.url);

          self._eventTarget.dispatchEvent(generateEvent('close'));
        }

        const timeout =
          self.reconnectInterval *
          Math.pow(self.reconnectDecay, self.reconnectAttempts);

        setTimeout(
          function() {
            self.reconnectAttempts++;
            self.open(true);
          },
          timeout > self.maxReconnectInterval
            ? self.maxReconnectInterval
            : timeout
        );
      }
    };

    ws.onmessage = function(event) {
      if (self.debug || Socket.debugAll)
        console.debug('Socket', 'onmessage', self.url, event.data);

      const e = generateEvent('message');
      e.data = event.data;

      self._eventTarget.dispatchEvent(e);
    };

    ws.onerror = function(event) {
      if (self.debug || Socket.debugAll)
        console.debug('Socket', 'onerror', self.url, event);

      self._eventTarget.dispatchEvent(generateEvent('error'));
    };
  }
}

/**
 * Create an event with the specified event name and arguments
 *
 * @param string eventName
 * @param string args
 */
const generateEvent = (eventName, args) => {
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent(eventName, false, false, args);
  return evt;
};

export default Socket;
